<template>
  <form @submit.prevent="submit">
    <FormMessage :errors="errors" v-if="errors" />
    <FormPassword
      name="new-password"
      v-model="resetPasswordFields.newPassword"
      testId="new-password-input"
      placeholder="New Password"
      label="New Password *"
      :errors="validation.fields.newPassword.errors"
      :dirty="validation.fields.newPassword.dirty"
      :strength-meter="true"
      v-model:strength="passwordStrength1"
      :tabindex="1"
    />
    <div class="text-secondary my-2">
      * Passwords are recommended to be at least 16-characters,
      <a href="" @click.prevent="showPasswordTips" tabindex="2">check out our password tips.</a>
    </div>
    <FormPassword
      name="confirm-password"
      v-model="resetPasswordFields.confirmPassword"
      testId="confirm-password-input"
      placeholder="Confirm Password"
      label="Confirm Password *"
      :errors="validation.fields.confirmPassword.errors"
      :dirty="validation.fields.confirmPassword.dirty"
      :strength-meter="true"
      v-model:strength="passwordStrength2"
      :tabindex="3"
    />
    <FormInput
      v-if="resetPasswordFields.twoFactorRequired"
      type="password"
      name="authentication-code"
      v-model="resetPasswordFields.twoFactorCode"
      testId="authentication-code-input"
      placeholder="Authentication Code"
      label="Authentication Code *"
      :errors="validation.fields.twoFactorCode.errors"
      :dirty="validation.fields.twoFactorCode.dirty"
      :tabindex="4"
    />
    <div class="form-actions">
      <Btn
        variant="primary"
        type="submit"
        :disabled="!submittable"
        :loading="loading"
        @click="submit"
        data-test="reset-button"
        :tabindex="5"
        >Reset Password</Btn
      >
    </div>
  </form>
</template>

<script>
import { computed, defineAsyncComponent, reactive, ref } from "vue";
import Btn from "@/components/display/Btn";
import FormInput from "@/components/display/form/FormInput";
import FormMessage from "@/components/display/form/FormMessage";
import { fromServer } from "@/utils/translations";
import PasswordTipsModal from "@/components/smart/modals/PasswordTipsModal";
import { validations as resetPasswordRules } from "leatherman-js";
import store from "@/store";
import useValidator from "@/utils/useValidator";

export default {
  name: "ResetPasswordForm",
  components: {
    Btn,
    FormMessage,
    FormInput,
    FormPassword: defineAsyncComponent(() =>
      import(/* webpackChunkName: "form-password" */ "@/components/display/form/FormPassword")
    ),
  },
  props: {
    authToken: String,
    requestId: String,
    userId: String,
  },
  setup(props, { emit }) {
    // display variables
    const loading = ref(false);
    const errors = ref(null);
    const hasCell = ref(false);
    const passwordStrength1 = ref(0);
    const passwordStrength2 = ref(0);

    // login variables and validation
    const resetPasswordFields = reactive({
      newPassword: null,
      confirmPassword: null,
      twoFactorRequired: false,
      twoFactorCode: null,
    });
    const { validator } = useValidator();
    const validation = validator(
      resetPasswordFields,
      resetPasswordRules.resetPassword,
      {},
      {
        newPassword: {
          required: "New password is required",
          password: "Password is too weak",
          matchField: "Passwords must match",
        },
        confirmPassword: {
          required: "Password confirmation required",
          matchField: "Passwords must match",
        },
        twoFactorCode: {
          regex: "Invalid Authentication Code",
        },
      }
    );
    const submittable = computed(
      () =>
        !loading.value &&
        passwordStrength1.value >= 3 &&
        passwordStrength2.value >= 3 &&
        !validation.errors.length
    );

    // submit the form
    const submit = () => {
      errors.value = null;

      validation.validate().then(() => {
        loading.value = true;
        store
          .dispatch("auth/resetPassword", {
            newPassword: resetPasswordFields.newPassword,
            confirmPassword: resetPasswordFields.confirmPassword,
            twoFactorCode: resetPasswordFields.twoFactorCode,
            authToken: props.authToken,
            requestId: props.requestId,
            userId: props.userId,
          })
          .then(() => {
            emit("success");
          })
          .catch((e) => {
            // if two factor is required, show the two factor code field
            if (e.twoFactorRequired) {
              resetPasswordFields.twoFactorRequired = true;
              // if the user has a cell number available for two factor, show the sms button
              hasCell.value = !!e.hasCell;
              validation.reset();
            } else {
              errors.value = fromServer(e.request.response);
            }
          })
          .finally(() => {
            loading.value = false;
          });
      });
    };

    const showPasswordTips = () => {
      store.dispatch("modals/open", { template: PasswordTipsModal });
    };

    return {
      errors,
      resetPasswordFields,
      loading,
      submit,
      validation,
      showPasswordTips,
      passwordStrength1,
      passwordStrength2,
      submittable,
    };
  },
};
</script>
